import { classifyInstallationTypology } from "@calcplan/ai-capabilities";
import type { SceneDocument, SceneElement, Vec2 } from "@calcplan/domain";
import { computePlanClearances } from "./plan-clearance";
import { readElementPlacementSurface } from "./placement";
import {
  renderedElementPlanGeometry,
  renderedElementVerticalIntervalMm,
} from "./rendered-envelope";

interface OrientedBox2D {
  center: Vec2;
  axisX: Vec2;
  axisZ: Vec2;
  halfX: number;
  halfZ: number;
}

export interface SceneCollision {
  kind: "element" | "wall";
  id: string;
  name: string;
}

/** Largest dimension of a small wall-mounted accessory (outlet, switch, point) that still reads as a fixture without a name. */
const FIXTURE_MAX_DIMENSION_MM = 200;
const FIXTURE_MAX_DEPTH_MM = 80;

/**
 * A point of installation embedded in or flush with a wall — outlet, switch,
 * light point, valve, tap. Furniture placed against that wall meets it with a
 * cut-out or an access, never by standing 60 mm away from the wall; so these
 * elements are installation interferences to report, not collision bodies.
 * Language decides first; a nameless accessory of a few centimetres counts too.
 */
export function isWallFixtureElement(element: SceneElement): boolean {
  const typology = classifyInstallationTypology({
    name: element.name,
    category: element.catalogRef?.itemId ?? null,
    dimensionsMm: element.dimensionsMm,
  });
  if (typology.kind === "fixture") return true;
  const { width, height, depth } = element.dimensionsMm;
  return typology.kind === "unknown"
    && Math.max(width, height) <= FIXTURE_MAX_DIMENSION_MM
    && depth <= FIXTURE_MAX_DEPTH_MM;
}

export interface SceneInstallationCheck {
  support: {
    expected: "wall" | "floor" | "ceiling" | "element" | "unconstrained";
    status: "supported" | "gap" | "penetrating" | "unsupported";
    surfaceId: string | null;
    gapMm: number | null;
  };
  floor: ReturnType<typeof checkElementGrounded>;
  ceiling: {
    status: "touching" | "clear" | "penetrating";
    ceilingElevationMm: number;
    gapMm: number;
  };
  nearestWalls: Array<{ wallId: string; gapMm: number }>;
  nearestElements: Array<{
    elementId: string;
    name: string;
    planGapMm: number;
    verticalGapMm: number;
    planOverlapMm: number;
    verticalOverlapMm: number;
    distanceMm: number;
    position: "above" | "below" | "beside";
    relation: "separate" | "touching" | "overlapping";
    /** Ponto de instalação embutido na parede (tomada, interruptor, registro): interferência a prever, não colisão. */
    fixture?: boolean;
  }>;
}

export function checkElementClearance(
  scene: SceneDocument,
  element: SceneElement,
): { collisions: SceneCollision[]; interferences: SceneCollision[]; clearancesMm: Record<string, number> } {
  const selectedBox = elementBox(element);
  const selectedVertical = verticalInterval(element);
  const visibleLayers = new Map(scene.layers.map((layer) => [layer.id, layer]));
  const collisions: SceneCollision[] = [];
  const interferences: SceneCollision[] = [];
  const selfIsFixture = isWallFixtureElement(element);

  for (const candidate of scene.elements) {
    if (candidate.id === element.id || !candidate.visible) continue;
    if (visibleLayers.get(candidate.layerId)?.visible === false) continue;
    if (!intervalsPenetrate(selectedVertical, verticalInterval(candidate))) continue;
    if (boxesPenetrate(selectedBox, elementBox(candidate))) {
      // An outlet behind a cabinet is a cut-out to plan, not a collision that
      // pushes the cabinet off the wall. Keep it visible as an interference.
      if (!selfIsFixture && isWallFixtureElement(candidate)) {
        interferences.push({ kind: "element", id: candidate.id, name: candidate.name });
        continue;
      }
      collisions.push({ kind: "element", id: candidate.id, name: candidate.name });
    }
  }

  for (const wall of scene.walls) {
    if (visibleLayers.get(wall.layerId)?.visible === false) continue;
    const level = scene.levels.find((candidate) => candidate.id === wall.levelId);
    const wallVertical: [number, number] = [
      level?.elevationMm ?? 0,
      (level?.elevationMm ?? 0) + wall.heightMm,
    ];
    if (!intervalsPenetrate(selectedVertical, wallVertical)) continue;
    const box = wallBox(wall.startMm, wall.endMm, wall.thicknessMm);
    if (box && boxesPenetrate(selectedBox, box)) {
      collisions.push({ kind: "wall", id: wall.id, name: `Parede ${wall.id}` });
    }
  }

  return {
    collisions,
    interferences,
    clearancesMm: Object.fromEntries(
      computePlanClearances(scene, element).map((clearance) => [
        clearance.direction,
        Math.round(clearance.distanceMm * 10) / 10,
      ]),
    ),
  };
}

export function checkElementGrounded(
  scene: SceneDocument,
  element: SceneElement,
): { status: "grounded" | "floating" | "buried"; floorElevationMm: number; gapMm: number } {
  const baseMm = renderedElementVerticalIntervalMm(element)[0];
  const floorElevationMm = scene.levels.length
    ? scene.levels.reduce((closest, level) => (
        Math.abs(level.elevationMm - baseMm) < Math.abs(closest - baseMm)
          ? level.elevationMm
          : closest
      ), scene.levels[0]!.elevationMm)
    : 0;
  const gapMm = Math.round((baseMm - floorElevationMm) * 10) / 10;
  return {
    status: Math.abs(gapMm) <= 2 ? "grounded" : gapMm > 0 ? "floating" : "buried",
    floorElevationMm,
    gapMm,
  };
}

/**
 * Measures the relationships that make an element physically credible.
 *
 * Clearance answers "does it collide?". This check answers the complementary
 * questions the agent needs after placing furniture: is a floor item actually
 * supported, is a wall-mounted item on the recorded wall face, and how far are
 * the nearest pieces and walls? All values come from the scene contract, so a
 * viewport inspection can independently compare them with the rendered mesh.
 */
export function checkElementInstallation(
  scene: SceneDocument,
  element: SceneElement,
): SceneInstallationCheck {
  const floor = checkElementGrounded(scene, element);
  const selectedVertical = verticalInterval(element);
  const level = scene.levels.length
    ? scene.levels.reduce((closest, candidate) => (
        Math.abs(candidate.elevationMm - floor.floorElevationMm) < Math.abs(closest.elevationMm - floor.floorElevationMm)
          ? candidate
          : closest
      ), scene.levels[0]!)
    : null;
  const ceilingElevationMm = (level?.elevationMm ?? 0) + (level?.heightMm ?? 2_700);
  const ceilingGapMm = rounded(ceilingElevationMm - selectedVertical[1]);
  const ceiling: SceneInstallationCheck["ceiling"] = {
    status: Math.abs(ceilingGapMm) <= 2 ? "touching" : ceilingGapMm > 0 ? "clear" : "penetrating",
    ceilingElevationMm,
    gapMm: ceilingGapMm,
  };
  const surface = readElementPlacementSurface(scene, element);
  const nearestWalls = scene.walls
    .map((wall) => {
      const box = wallBox(wall.startMm, wall.endMm, wall.thicknessMm);
      return box ? { wallId: wall.id, gapMm: rounded(boxSeparation(elementBox(element), box)) } : null;
    })
    .filter((entry): entry is { wallId: string; gapMm: number } => Boolean(entry))
    .sort((left, right) => left.gapMm - right.gapMm)
    .slice(0, 4);
  const nearestElements = scene.elements
    .filter((candidate) => candidate.id !== element.id && candidate.visible)
    .map((candidate) => {
      const candidateVertical = verticalInterval(candidate);
      const planSignedMm = boxSignedSeparation(elementBox(element), elementBox(candidate));
      const verticalSignedMm = intervalSignedGap(selectedVertical, candidateVertical);
      const planGapMm = Math.max(0, planSignedMm);
      const verticalGapMm = Math.max(0, verticalSignedMm);
      const planOverlapMm = Math.max(0, -planSignedMm);
      const verticalOverlapMm = Math.max(0, -verticalSignedMm);
      const position: SceneInstallationCheck["nearestElements"][number]["position"] = candidateVertical[1] <= selectedVertical[0] + 2
        ? "below"
        : candidateVertical[0] >= selectedVertical[1] - 2
          ? "above"
          : "beside";
      return {
        elementId: candidate.id,
        name: candidate.name,
        planGapMm: rounded(planGapMm),
        verticalGapMm: rounded(verticalGapMm),
        planOverlapMm: rounded(planOverlapMm),
        verticalOverlapMm: rounded(verticalOverlapMm),
        distanceMm: rounded(Math.hypot(planGapMm, verticalGapMm)),
        position,
        relation: planSignedMm < -1 && verticalSignedMm < -1
          ? "overlapping" as const
          : (Math.abs(planSignedMm) <= 2 && verticalOverlapMm > 1)
            || (Math.abs(verticalSignedMm) <= 2 && planOverlapMm > 1)
            ? "touching" as const
            : "separate" as const,
        ...(isWallFixtureElement(candidate) ? { fixture: true } : {}),
      };
    })
    .sort((left, right) => left.distanceMm - right.distanceMm)
    .slice(0, 8);

  if (surface?.kind === "wall") {
    const gapMm = rounded(wallSurfaceGap(element, surface.pointMm, surface.normal));
    return {
      support: {
        expected: "wall",
        status: Math.abs(gapMm) <= 2 ? "supported" : gapMm > 0 ? "gap" : "penetrating",
        surfaceId: surface.wallId ?? null,
        gapMm,
      },
      floor,
      ceiling,
      nearestWalls,
      nearestElements,
    };
  }

  if (surface?.kind === "floor") {
    return {
      support: {
        expected: "floor",
        status: Math.abs(floor.gapMm) <= 2 ? "supported" : floor.gapMm > 0 ? "gap" : "penetrating",
        surfaceId: null,
        gapMm: floor.gapMm,
      },
      floor,
      ceiling,
      nearestWalls,
      nearestElements,
    };
  }

  if (Math.abs(floor.gapMm) <= 2) {
    return {
      support: { expected: "floor", status: "supported", surfaceId: null, gapMm: floor.gapMm },
      floor,
      ceiling,
      nearestWalls,
      nearestElements,
    };
  }

  const supportingElement = nearestElements.find((candidate) => (
    candidate.position === "below"
    && candidate.verticalGapMm <= 2
    && candidate.planOverlapMm > 2
  ));
  if (supportingElement) {
    return {
      support: {
        expected: "element",
        status: supportingElement.verticalGapMm <= 2 ? "supported" : "gap",
        surfaceId: supportingElement.elementId,
        gapMm: supportingElement.verticalGapMm,
      },
      floor,
      ceiling,
      nearestWalls,
      nearestElements,
    };
  }

  if (Math.abs(ceiling.gapMm) <= 2) {
    return {
      support: { expected: "ceiling", status: "supported", surfaceId: null, gapMm: ceiling.gapMm },
      floor,
      ceiling,
      nearestWalls,
      nearestElements,
    };
  }

  const intentionallyFloating = floor.gapMm > 2 && nearestWalls.some((wall) => wall.gapMm <= 2);
  return {
    support: {
      expected: floor.gapMm <= 2 ? "floor" : intentionallyFloating ? "wall" : "unconstrained",
      status: Math.abs(floor.gapMm) <= 2
        ? "supported"
        : intentionallyFloating
          ? "unsupported"
          : floor.gapMm < -2
            ? "penetrating"
            : "unsupported",
      surfaceId: null,
      gapMm: floor.gapMm,
    },
    floor,
    ceiling,
    nearestWalls,
    nearestElements,
  };
}

function elementBox(element: SceneElement): OrientedBox2D {
  const geometry = renderedElementPlanGeometry(element);
  const angle = element.transform.rotationRad[1];
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    center: geometry.centerMm,
    axisX: [cos, sin],
    axisZ: [-sin, cos],
    halfX: geometry.widthMm / 2,
    halfZ: geometry.depthMm / 2,
  };
}

function wallBox(start: Vec2, end: Vec2, thicknessMm: number): OrientedBox2D | null {
  const dx = end[0] - start[0];
  const dz = end[1] - start[1];
  const length = Math.hypot(dx, dz);
  if (length < 1e-6) return null;
  const axisX: Vec2 = [dx / length, dz / length];
  return {
    center: [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2],
    axisX,
    axisZ: [-axisX[1], axisX[0]],
    halfX: length / 2,
    halfZ: Math.max(0.5, thicknessMm / 2),
  };
}

/** Separating-axis test. Contact is allowed; positive penetration is collision. */
function boxesPenetrate(left: OrientedBox2D, right: OrientedBox2D): boolean {
  const centerDelta: Vec2 = [right.center[0] - left.center[0], right.center[1] - left.center[1]];
  for (const axis of [left.axisX, left.axisZ, right.axisX, right.axisZ]) {
    const distance = Math.abs(dot(centerDelta, axis));
    const leftRadius = projectedRadius(left, axis);
    const rightRadius = projectedRadius(right, axis);
    if (leftRadius + rightRadius - distance <= 1) return false;
  }
  return true;
}

function projectedRadius(box: OrientedBox2D, axis: Vec2): number {
  return Math.abs(dot(box.axisX, axis)) * box.halfX
    + Math.abs(dot(box.axisZ, axis)) * box.halfZ;
}

/** Positive separation, zero contact/overlap, using the OBB separating axes. */
function boxSeparation(left: OrientedBox2D, right: OrientedBox2D): number {
  return Math.max(0, boxSignedSeparation(left, right));
}

/** Positive gap, zero contact, negative overlap on the least-separated axis. */
function boxSignedSeparation(left: OrientedBox2D, right: OrientedBox2D): number {
  const centerDelta: Vec2 = [right.center[0] - left.center[0], right.center[1] - left.center[1]];
  let separation = Number.NEGATIVE_INFINITY;
  for (const axis of [left.axisX, left.axisZ, right.axisX, right.axisZ]) {
    separation = Math.max(
      separation,
      Math.abs(dot(centerDelta, axis)) - projectedRadius(left, axis) - projectedRadius(right, axis),
    );
  }
  return separation;
}

function wallSurfaceGap(
  element: SceneElement,
  pointMm: [number, number, number],
  normal: [number, number, number],
): number {
  const box = elementBox(element);
  const horizontalNormal: Vec2 = [normal[0], normal[2]];
  const centerFromSurface: Vec2 = [
    box.center[0] - pointMm[0],
    box.center[1] - pointMm[2],
  ];
  return dot(centerFromSurface, horizontalNormal) - projectedRadius(box, horizontalNormal);
}

function verticalInterval(element: SceneElement): [number, number] {
  return renderedElementVerticalIntervalMm(element);
}

function intervalsPenetrate(left: [number, number], right: [number, number]): boolean {
  return Math.min(left[1], right[1]) - Math.max(left[0], right[0]) > 1;
}

function intervalGap(left: [number, number], right: [number, number]): number {
  return Math.max(0, intervalSignedGap(left, right));
}

function intervalSignedGap(left: [number, number], right: [number, number]): number {
  return Math.max(left[0] - right[1], right[0] - left[1]);
}

function rounded(value: number): number {
  const result = Math.round(value * 10) / 10;
  return Object.is(result, -0) ? 0 : result;
}

function dot(left: Vec2, right: Vec2): number {
  return left[0] * right[0] + left[1] * right[1];
}
